import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";

import { InfoOutlined } from "@mui/icons-material";
import { Modal, PopperProps } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { useWindowWidth } from "@react-hook/window-size";
import { themePalette } from "../../theme";
import { getReqDisplayDate } from "../../utils/dateUtils";
import VideoEmbed from "../../components/shared/Video/VideoEmbed";
import { TestResults } from "../../components/TestResults/TestResults";
import {
  LandingGeneralContainer,
  LandingPageAccordionWrapper,
  LandingPageDesktopLeftWrapper,
  LandingPageDesktopRightWrapper,
  LandingPageDesktopWrapper,
  LandingPageDesktopWrapperPadding,
  LandingPageTabletMobileRowWrapper,
  LandingPageTabletMobileWrapper,
  LandingPageTitleWrapper,
  LandingPageWrapper,
  LandingTypeformWrapper,
  LandingPretestConsentTitle,
  BabyPeekToolTip,
  BabyPeekAvailable,
  LandingPageBillingViewBill,
  LandingPageRequisitionSelectWrapper,
  LandingPageRequisitionSelectWrapperItem,
  LandingPageMultipleRequisitionTitleWrapper,
  LandingPretestConsentSpinner,
  LandingPretestModal,
} from "./Landing.styles";
import { Card } from "../../components/shared/Cards/Card";
import {
  Accordions,
  LinkedAccordian,
} from "../../components/shared/Accordion/Accordions";
import { GetTypographyContent } from "../../components/shared/Typography/Typography";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import {
  BABYPEEK_URL,
  BILLING_MULTIPLE_URL,
  BILLING_URL,
  HELP_PAGE_URL,
  GC_LEARN_MORE_URL,
} from "../../utils/urlConstants";
import {
  getBillPreviews,
  getAllRequisitions,
  getSelectedRequisition,
  getUserAuth,
} from "../../recoil/selectors";
import {
  landingPageAccordions,
  landingPageCard,
  landingPageGCLearnMoreCard,
} from "./LandingHelpData";
import { shouldBlockGCBooking } from "../../utils/gcBookingUtils";
import {
  isMissingBillingInformation,
  isMissingBillingAddress,
} from "../../components/Profile/helpers";
import {
  babyPeekStatus,
  failedPaymentBillPkState,
  patientProfileState,
  selectedRequisitionIndex,
  showABNForm,
} from "../../recoil/atoms";
import { Typeform } from "../../components/Typeform/Typeform";
import { UpdateConsent } from "./Services/LandingService";
import {
  ConsentStatus,
  STANDALONE_FETAL_SEX_CARD_STATE,
  UserProfileAuthClientResponse,
} from "../../recoil/types";
import { GetPhiRequest } from "../../components/Profile/Services/UpdateProfileInfoService";
import { ProfileAlabamaWarning } from "../../components/Profile/ProfileAlabamaWarning";
import { BabyPeekOptIn } from "../../components/BabyPeek/BabyPeekOptIn";
import BabyPeekHeader from "../../components/BabyPeek/BabyPeekIcons/BabyPeekHeader.png";
import { BabyPeekButton } from "../../components/BabyPeek/BabyPeekButton";
import { DetailedResultsShareBabyPeek } from "../../components/DetailedResults/DetailedResultsShareBabyPeek";
import { LandingFeedbackBanner } from "./LandingFeedbackBanner";
import { LandingFeedbackCard } from "./LandingFeedbackCard";
import { SpinnerLoading } from "../../components/shared/Spinner/SpinnerLoading";
import { Banner } from "../../components/Banner/Banner";
import { MissingBillingInformationModal } from "../../components/MissingBillingInformation/MissingBillingInformationModal";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { useBasePath } from "../../utils/hooks/useBathPath";
import { StandaloneFetalSexCard } from "../../components/StandaloneFetalSex/StandaloneFetalSexCard";
import { getFetalSexResultBlockValue } from "../../components/shared/FetalSexResultBlock/fetalSexResultUtils";
import {
  STANDALONE_FETAL_SEX_EXPERIENCE,
  useStandaloneFetalSexRollout,
} from "../../utils/hooks/useStandaloneFetalSexRollout";

const babyPeekUnavailableText =
  "We were unable to provide you with BabyPeek results. BabyPeek is not available for twin pregnancies, pregnancies conceived with a donor egg, surrogate pregnancies, or following a No Call or redraw request.";

const Landing = (): React.ReactElement => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const billPreviews = useRecoilValue(getBillPreviews);

  // Helper function to determine if billing information collection is needed
  const needsBillingInformationCollection = (
    patientInfo?: UserProfileAuthClientResponse,
  ): boolean =>
    isMissingBillingInformation(patientInfo) ||
    isMissingBillingAddress(patientInfo);

  const windowWidth = useWindowWidth();

  const requisitions = useRecoilValue(getAllRequisitions);
  const specificRequisition = useRecoilValue(getSelectedRequisition);
  const standaloneFetalSexCardState =
    specificRequisition?.standalone_fetal_sex_card_state as string | undefined;
  const standaloneFetalSexResult =
    specificRequisition?.standalone_fetal_sex_result;
  const standaloneFetalSexExperience = useStandaloneFetalSexRollout();
  const isStandaloneFetalSexEnabled =
    standaloneFetalSexExperience === STANDALONE_FETAL_SEX_EXPERIENCE.STANDALONE;
  const showStandaloneFetalSexCard =
    isStandaloneFetalSexEnabled &&
    (standaloneFetalSexCardState ===
      STANDALONE_FETAL_SEX_CARD_STATE.PROCESSING ||
      (standaloneFetalSexCardState ===
        STANDALONE_FETAL_SEX_CARD_STATE.NON_SEX_RESULT &&
        Boolean(standaloneFetalSexResult)) ||
      ((standaloneFetalSexCardState ===
        STANDALONE_FETAL_SEX_CARD_STATE.SEX_REVEAL ||
        standaloneFetalSexCardState === "SHOW_FETAL_SEX") &&
        Boolean(
          getFetalSexResultBlockValue(
            standaloneFetalSexResult?.standalone_fetal_sex,
          ),
        )));
  const [selectedRequisition, setSelectedRequisitionIndex] = useRecoilState(
    selectedRequisitionIndex,
  );

  const failedPaymentBillPk = useRecoilValue(failedPaymentBillPkState);

  const history = useHistory();
  const auth = useRecoilValue(getUserAuth);
  const basePath = useBasePath();
  const [patientInfo, setPatientInfo] = useRecoilState(patientProfileState);
  const [showABNFormPatient, setShowABNFormPatient] =
    useRecoilState(showABNForm);
  const [missingBillingInfoModalOpen, setMissingBillingInfoModalOpen] =
    useState(false);
  const [submittingConsent, setSubmittingConsent] = useState(false);

  // Helper function to get banner configuration based on what's missing
  const getBillingBannerConfig = () => {
    const missingInsurance = isMissingBillingInformation(patientInfo);
    const missingAddress = isMissingBillingAddress(patientInfo);

    if (missingInsurance) {
      // Insurance missing (regardless of address) - prioritize insurance first
      return {
        title: t("landing.banners.missingBillingInfo.title"),
        actionText: t("landing.banners.reviewNow"),
      };
    }
    if (missingAddress) {
      // Only address missing (insurance is complete)
      return {
        title: t("landing.banners.missingBillingAddress.title"),
        actionText: t("landing.banners.editNow"),
      };
    }
    return null; // Nothing missing
  };

  const [tooltip, setTooltip] = useState(false);
  const [showBabyPeekPopUp, setShowBabyPeekPopUp] = useState(false);
  const babyPeekStatusFlag = useRecoilValue(babyPeekStatus);

  const shouldBlockBooking = shouldBlockGCBooking(specificRequisition);
  const [showOutcomesBanner, setShowOutcomesBanner] = useState(false);
  const [showPatientConsent, setShowPatientConsent] = useState(false);
  const [consentRequestFailed, setConsentRequestFailed] = useState(false);
  const [sentBillingInfoTracking, setSentBillingInfoTracking] = useState(false);

  const [sentBillingTracking, setSentBillingTracking] = useState(false);
  const [sentFailedPaymentTracking, setSentFailedPaymentTracking] =
    useState(false);

  const multipleBills = requisitions?.filter(
    (req) => req.portal_bill_status === "outstanding",
  ).length;

  useEffect(() => {
    const localPopUp = localStorage.getItem("babyPeekPopUpDismmised");
    const sessionPopUp = sessionStorage.getItem("babyPeekPopUpDismmised");

    if (localPopUp !== "true" && sessionPopUp !== "true") {
      setShowBabyPeekPopUp(true);
    }
  }, []);

  useEffect(() => {
    if (
      failedPaymentBillPk &&
      !sentFailedPaymentTracking &&
      auth?.access_token
    ) {
      TrackAnalyticsEvent(
        AnalyticsEventNames.impression,
        auth.access_token,
        auth?.user_id,
        {
          element_name: "failed_payment_banner_shown",
          page_name: basePath,
        },
      );
      setSentFailedPaymentTracking(true);
    }
    if (
      needsBillingInformationCollection(patientInfo) &&
      !sentBillingInfoTracking &&
      auth?.access_token
    ) {
      TrackAnalyticsEvent(
        AnalyticsEventNames.impression,
        auth.access_token,
        undefined,
        {
          element_name: AnalyticsEventNames.MissingInsuranceInfoBannerShown,
          page_name: basePath,
        },
      );
      setSentBillingInfoTracking(true);
    }
    if (
      requisitions?.filter((req) => req.portal_bill_status === "outstanding")
        .length &&
      !sentBillingInfoTracking &&
      auth?.access_token
    ) {
      TrackAnalyticsEvent(
        AnalyticsEventNames.impression,
        auth?.access_token,
        auth?.user_id,
        {
          element_name: AnalyticsEventNames.OpenBillBannerShown,
          page_name: basePath,
        },
      );
      setSentBillingInfoTracking(true);
    }
  }, [
    requisitions,
    patientInfo,
    auth,
    failedPaymentBillPk,
    sentFailedPaymentTracking,
    sentBillingInfoTracking,
    basePath,
  ]);

  useEffect(() => {
    if (patientInfo?.user?.consent_status === ConsentStatus.INCOMPLETE) {
      setShowPatientConsent(true);
      if (auth?.access_token) {
        TrackAnalyticsEvent(
          AnalyticsEventNames.impression,
          auth?.access_token,
          auth?.user_id,
          {
            element_name: AnalyticsEventNames.PatientConsentBannerShown,
            page_name: basePath,
          },
        );
      }
    }
    if (patientInfo?.user?.consent_status === ConsentStatus.COMPLETE) {
      const timeout = setTimeout(() => {
        setShowPatientConsent(false);
      }, 5000);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [patientInfo?.user?.consent_status]);

  const dismissOutcomesBanner = () => {
    setShowOutcomesBanner(false);
    localStorage.setItem("seenOutcomesBanner", "true");
  };

  useEffect(() => {
    if (localStorage.getItem("seenOutcomesBanner") !== "true") {
      window.onbeforeunload = () => {
        localStorage.setItem("seenOutcomesBanner", "true");
      };
      setShowOutcomesBanner(true);
      if (auth?.access_token) {
        TrackAnalyticsEvent(
          AnalyticsEventNames.impression,
          auth?.access_token,
          auth?.user_id,
          {
            element_name: AnalyticsEventNames.PatientOutcomesBannerShown,
            page_name: basePath,
          },
        );
      }
    }
  }, []);

  useEffect(() => {
    if (
      !showABNFormPatient.loaded &&
      !showABNFormPatient.show &&
      requisitions?.[selectedRequisition]?.show_abn_form
    ) {
      setShowABNFormPatient({ loaded: true, show: true });
    }
  }, []);

  useEffect(() => {
    const getProfileInfo = async () => {
      const response = await GetPhiRequest(auth?.access_token);
      if (response?.ok) {
        const profile =
          (await response.json()) as UserProfileAuthClientResponse;
        setPatientInfo(profile);
      }
    };
    if (!patientInfo && auth) {
      getProfileInfo();
    }
  }, [patientInfo, auth]);

  const ConsentFormAction = () => {
    if (patientInfo?.user?.consent_status === ConsentStatus.INCOMPLETE) {
      if (submittingConsent) {
        return (
          <LandingPretestConsentSpinner>
            {" "}
            <SpinnerLoading />
          </LandingPretestConsentSpinner>
        );
      }
      return (
        <LandingPretestConsentTitle
          style={{ justifySelf: "end", cursor: "pointer" }}
          onClick={async () => {
            setSubmittingConsent(!submittingConsent);
            if (auth?.access_token) {
              TrackAnalyticsEvent(
                AnalyticsEventNames.interaction,
                auth?.access_token,
                auth?.user_id,
                {
                  interaction_type: "button_click",
                  button_text: "I AGREE",
                  element_name: AnalyticsEventNames.PatientConsentButtonClicked,
                  page_name: basePath,
                },
              );
            }

            if (patientInfo?.user) {
              const response = await UpdateConsent(
                {
                  consent_status: ConsentStatus.COMPLETE,
                  has_carrier_consent: true,
                  has_aneuploidy_consent: true,
                },
                auth?.access_token,
              );
              if (response.ok) {
                const updatedUserInfo = await response.json();
                setPatientInfo(
                  updatedUserInfo as UserProfileAuthClientResponse,
                );
              } else {
                setConsentRequestFailed(true);
              }
              setSubmittingConsent(false);
            }
          }}
        >
          {t("landing.consent.agree")}
        </LandingPretestConsentTitle>
      );
    }
    if (patientInfo?.user?.consent_status === ConsentStatus.COMPLETE) {
      return (
        <LandingPretestConsentSpinner>
          <FontAwesomeIcon
            icon={faCircleCheck}
            style={{
              color: themePalette.greenSuccess,
              width: "30px",
              height: "30px",
            }}
          />
        </LandingPretestConsentSpinner>
      );
    }
  };

  const ConsentForm = (
    <>
      <Modal
        open={consentRequestFailed}
        onClose={() => setConsentRequestFailed(false)}
        style={{
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
          width: windowWidth,
        }}
        sx={{
          "& .MuiModal-backdrop": {
            width: "inherit",
          },
        }}
      >
        <LandingPretestModal>
          <svg
            width="41"
            height="37"
            viewBox="0 0 41 37"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.1328 35.8672L3.38281 29.1172C2.46875 28.2734 2.46875 26.7969 3.38281 25.9531C4.22656 25.0391 5.70312 25.0391 6.54688 25.9531L9.5 28.8359V20.75H2.75C1.48438 20.75 0.5 19.7656 0.5 18.5C0.5 17.3047 1.48438 16.25 2.75 16.25H9.5H14H27.5V8.23438L24.5469 11.1172C23.7031 12.0312 22.2266 12.0312 21.3828 11.1172C20.4688 10.2734 20.4688 8.79688 21.3828 7.95312L28.1328 1.20312C28.9766 0.289062 30.4531 0.289062 31.2969 1.20312L38.0469 7.95312C38.9609 8.79688 38.9609 10.2734 38.0469 11.1172C37.2031 12.0312 35.7266 12.0312 34.8828 11.1172L32 8.23438V16.25H38.75C39.9453 16.25 41 17.3047 41 18.5C41 19.7656 39.9453 20.75 38.75 20.75H14V28.8359L16.8828 25.9531C17.7266 25.0391 19.2031 25.0391 20.0469 25.9531C20.9609 26.7969 20.9609 28.2734 20.0469 29.1172L13.2969 35.8672C12.4531 36.7812 10.9766 36.7812 10.1328 35.8672ZM9.5 14V5C9.5 3.80469 10.4844 2.75 11.75 2.75C12.9453 2.75 14 3.80469 14 5V14H9.5ZM32 23V32C32 33.2656 30.9453 34.25 29.75 34.25C28.4844 34.25 27.5 33.2656 27.5 32V23H32Z"
              fill="#F0604B"
            />
          </svg>
          <span> {t("landing.consent.errorTitle")}</span>
          <p>{t("landing.consent.errorMessage")}</p>
          <LandingPageBillingViewBill
            onClick={() => setConsentRequestFailed(false)}
          >
            {t("landing.banners.ok")}
          </LandingPageBillingViewBill>
        </LandingPretestModal>
      </Modal>
      {patientInfo?.user && showPatientConsent && (
        <Banner
          bannerType="information"
          bannerActionCTA={ConsentFormAction()}
          bannerText={t("landing.consent.bannerText")}
          bannerTitle={t("landing.consent.bannerTitle")}
        />
      )}
      {patientInfo?.user?.survey_status === ConsentStatus.INCOMPLETE &&
        Boolean(requisitions?.length) && (
          <LandingTypeformWrapper>
            <Typeform
              typeformID="jvuge1VD"
              onSubmit={async () => {
                if (patientInfo?.user) {
                  const response = await UpdateConsent(
                    {
                      survey_status: ConsentStatus.COMPLETE,
                    },
                    auth?.access_token,
                  );
                  if (response?.ok) {
                    const updatedUserInfo = await response.json();
                    setPatientInfo(
                      updatedUserInfo as UserProfileAuthClientResponse,
                    );
                  }
                }
              }}
            />
          </LandingTypeformWrapper>
        )}
    </>
  );

  const RequisitionPicker = (
    <div>
      <LandingPageMultipleRequisitionTitleWrapper>
        {t("landing.requisitionPicker.title")}
      </LandingPageMultipleRequisitionTitleWrapper>
      <LandingPageRequisitionSelectWrapper
        value={selectedRequisition}
        onChange={(e) => setSelectedRequisitionIndex(e.target.value as number)}
        sx={{
          ".MuiOutlinedInput-notchedOutline": { borderStyle: "none" },
        }}
      >
        {requisitions?.map((req, index) => (
          <LandingPageRequisitionSelectWrapperItem
            key={req.req_identifier}
            value={index}
          >{`${getReqDisplayDate(req)} ${
            index === 0 ? t("landing.requisitionPicker.mostRecent") : ""
          }`}</LandingPageRequisitionSelectWrapperItem>
        ))}
      </LandingPageRequisitionSelectWrapper>
    </div>
  );

  const getLandingContent = () => {
    if (deviceType === "desktop") {
      return (
        <>
          <LandingPageWrapper data-testid="LandingPageWrapper">
            <LandingPageDesktopWrapperPadding>
              {failedPaymentBillPk && (
                <Banner
                  bannerType="warning"
                  bannerActionCTA={t("landing.banners.reviewNow")}
                  bannerTitle={t("landing.banners.failedPayment.title")}
                  bannerActionOnClick={() => {
                    if (auth?.access_token) {
                      TrackAnalyticsEvent(
                        AnalyticsEventNames.FailedPaymentBannerClicked,
                        auth.access_token,
                        auth?.user_id,
                        {
                          interaction_type: "button_click",
                          button_text: "REVIEW NOW",
                          element_name: "failed_payment_banner_clicked",
                          page_name: basePath,
                        },
                      );
                    }

                    history.push(`${BILLING_URL}/${failedPaymentBillPk}`);
                  }}
                />
              )}
              {(() => {
                const bannerConfig = getBillingBannerConfig();
                return (
                  bannerConfig && (
                    <Banner
                      bannerType="warning"
                      bannerActionCTA={bannerConfig.actionText}
                      bannerTitle={bannerConfig.title}
                      bannerActionOnClick={() => {
                        setMissingBillingInfoModalOpen(true);
                        if (auth?.access_token) {
                          TrackAnalyticsEvent(
                            AnalyticsEventNames.interaction,
                            auth.access_token,
                            undefined,
                            {
                              interaction_type: "button_click",
                              button_text: bannerConfig.actionText,
                              element_name:
                                AnalyticsEventNames.MissingBillingInfoButtonClicked,
                              page_name: basePath,
                            },
                          );
                        }
                      }}
                    />
                  )
                );
              })()}
              {ConsentForm}

              {showOutcomesBanner && Boolean(requisitions?.length) && (
                <Banner
                  bannerType="information"
                  bannerActionCTA={t("landing.banners.ok")}
                  bannerText={t("landing.banners.outcomes.text")}
                  bannerTitle={t("landing.banners.outcomes.title")}
                  bannerActionOnClick={() => {
                    if (auth?.access_token) {
                      TrackAnalyticsEvent(
                        AnalyticsEventNames.interaction,
                        auth?.access_token,
                        auth?.user_id,
                        {
                          interaction_type: "button_click",
                          button_text: "OK",
                          element_name:
                            AnalyticsEventNames.PatientOutcomesButtonClicked,
                          page_name: basePath,
                        },
                      );
                    }
                    dismissOutcomesBanner();
                  }}
                />
              )}

              {Boolean(
                requisitions?.filter(
                  (req) => req.portal_bill_status === "outstanding",
                ).length,
              ) && (
                <Banner
                  bannerType="billing"
                  bannerActionCTA={t("landing.banners.viewMyBill")}
                  bannerText=""
                  bannerTitle={t("landing.banners.billReady.title")}
                  bannerActionOnClick={() => {
                    if (auth?.access_token) {
                      TrackAnalyticsEvent(
                        AnalyticsEventNames.interaction,
                        auth?.access_token,
                        auth?.user_id,
                        {
                          interaction_type: "button_click",
                          button_text: "View My Bill",
                          element_name:
                            AnalyticsEventNames.OpenBillButtonClicked,
                          page_name: basePath,
                        },
                      );
                    }
                    history.push(
                      multipleBills && multipleBills > 1
                        ? `${BILLING_MULTIPLE_URL}`
                        : `${BILLING_URL}/${
                            requisitions?.filter(
                              (req) => req.portal_bill_status === "outstanding",
                            )[selectedRequisition].bill_pk
                          }`,
                    );
                  }}
                />
              )}
              <LandingFeedbackBanner />
              <LandingPageDesktopWrapper>
                <LandingPageDesktopLeftWrapper
                  $showStandaloneFetalSexCard={showStandaloneFetalSexCard}
                  requisitionCount={requisitions?.length || 0}
                  showBabyPeek={Boolean(
                    requisitions &&
                    requisitions[selectedRequisition]?.reveal_status ===
                      "pending",
                  )}
                  babyPeekAvailable={Boolean(
                    requisitions &&
                    (requisitions[selectedRequisition]?.reveal_status ===
                      "available" ||
                      requisitions[selectedRequisition]?.reveal_status ===
                        "removed"),
                  )}
                >
                  {requisitions && Boolean(requisitions?.length) && (
                    <>
                      <LandingPageTitleWrapper>
                        {GetTypographyContent({
                          content: t("landing.sections.yourTests"),
                          size: "h2",
                          textColor: "greyMedium",
                          isBold: true,
                        })}
                      </LandingPageTitleWrapper>
                      {requisitions.length > 1 && RequisitionPicker}
                      {specificRequisition && (
                        <LandingGeneralContainer
                          padding="0px"
                          key={specificRequisition.req_identifier}
                        >
                          <TestResults
                            requisitionID={specificRequisition.req_identifier}
                            standaloneFetalSexExperience={
                              standaloneFetalSexExperience
                            }
                          />
                        </LandingGeneralContainer>
                      )}
                      {specificRequisition && showStandaloneFetalSexCard && (
                        <StandaloneFetalSexCard
                          key={`standalone-fetal-sex-${specificRequisition.req_identifier}`}
                          requisitionID={specificRequisition.req_identifier}
                        />
                      )}
                    </>
                  )}

                  {Boolean(
                    requisitions &&
                    (requisitions[selectedRequisition]?.reveal_status ===
                      "available" ||
                      requisitions[selectedRequisition]?.reveal_status ===
                        "removed"),
                  ) && (
                    <LandingGeneralContainer padding="0px">
                      {requisitions &&
                      requisitions[selectedRequisition]?.reveal_status ===
                        "removed" ? (
                        <BabyPeekToolTip
                          onClick={() => setTooltip(!tooltip)}
                          PopperProps={
                            {
                              sx: {
                                "& .MuiTooltip-tooltip": {
                                  backgroundColor: "white",
                                  boxShadow: "0px 10px 20px rgb(0 0 0 / 10%)",
                                  color: "black",
                                  fontSize: "16px",
                                  fontWeight: 400,
                                  lineHeight: "150%",
                                  textAlign: "center",
                                  border: "1px solid #cbd5e0",
                                },
                              },
                              open: tooltip,
                            } as Partial<PopperProps>
                          }
                          title={babyPeekUnavailableText}
                        >
                          <div>
                            {t("landing.babyPeek.unavailableShort")}{" "}
                            <InfoOutlined />
                          </div>
                        </BabyPeekToolTip>
                      ) : (
                        <BabyPeekAvailable>
                          <div>
                            <img src={BabyPeekHeader} alt="BabyPeekHeader" />
                            <div>BabyPeek</div>
                          </div>
                          <div>
                            <BabyPeekButton
                              variant="filled"
                              content={<>{t("landing.babyPeek.viewResults")}</>}
                              onClick={() =>
                                history.push(
                                  `${BABYPEEK_URL}/${
                                    requisitions &&
                                    requisitions[selectedRequisition]
                                      ?.baby_peek_pk
                                  }/${
                                    babyPeekStatusFlag === "available"
                                      ? "summary"
                                      : "intro"
                                  }?loc=dashboard`,
                                )
                              }
                            />
                          </div>
                        </BabyPeekAvailable>
                      )}
                    </LandingGeneralContainer>
                  )}
                  {requisitions &&
                    requisitions[selectedRequisition]?.reveal_status ===
                      "pending" && (
                      <DetailedResultsShareBabyPeek
                        babyPeekPK={
                          requisitions[selectedRequisition]?.baby_peek_pk
                        }
                        page="Dashboard"
                      />
                    )}
                  <LandingGeneralContainer padding="20px">
                    <VideoEmbed
                      overlayText={
                        <>
                          {t("landing.videoOverlay.prefix")}Unity Complete
                          <sup>&reg;</sup>
                          {t("landing.videoOverlay.suffix")}
                        </>
                      }
                      requisitionID={
                        (requisitions &&
                          requisitions[selectedRequisition]?.req_identifier) ||
                        ""
                      }
                    />
                  </LandingGeneralContainer>
                  <LandingFeedbackCard buttonSize="medium" />
                  <LandingGeneralContainer padding="0px">
                    {shouldBlockBooking ? (
                      <Card
                        {...landingPageGCLearnMoreCard(deviceType, t)}
                        buttons={[
                          {
                            buttonAction: () => {
                              TrackAnalyticsEvent(
                                AnalyticsEventNames.GCLearnMoreButtonClicked,
                                auth?.access_token,
                              );
                              history.push(GC_LEARN_MORE_URL);
                            },
                            buttonText: t("landing.gcCard.learnMore"),
                          },
                        ]}
                      />
                    ) : (
                      <Card
                        {...landingPageCard(deviceType, t)}
                        buttons={[
                          {
                            buttonAction: () => history.push("scheduling"),
                            buttonText: t("landing.gcCard.bookConsultation"),
                          },
                        ]}
                      />
                    )}
                  </LandingGeneralContainer>
                </LandingPageDesktopLeftWrapper>
                <LandingPageDesktopRightWrapper>
                  <LandingPageTitleWrapper>
                    {GetTypographyContent({
                      content: t("landing.sections.haveAQuestion"),
                      size: "large",
                      textColor: "greyMedium",
                      isBold: true,
                    })}
                  </LandingPageTitleWrapper>
                  <LandingGeneralContainer padding="0px">
                    <LandingPageAccordionWrapper>
                      <Accordions
                        data={landingPageAccordions(
                          (requisitions &&
                            requisitions[selectedRequisition]
                              ?.req_identifier) ??
                            "",
                          t,
                        )}
                        size="small"
                        isLinked
                      />
                      <LinkedAccordian
                        id="help"
                        key="help"
                        panelKey="help"
                        title={t("landing.faq.visitFaqs")}
                        onClick={() => {
                          history.push(HELP_PAGE_URL);
                        }}
                        size="small"
                      />
                    </LandingPageAccordionWrapper>
                  </LandingGeneralContainer>
                </LandingPageDesktopRightWrapper>
              </LandingPageDesktopWrapper>
            </LandingPageDesktopWrapperPadding>
          </LandingPageWrapper>
        </>
      );
    }
    return (
      <LandingPageWrapper data-testid="LandingPageWrapper">
        <LandingPageTabletMobileWrapper>
          {failedPaymentBillPk && (
            <Banner
              bannerType="warning"
              bannerActionCTA={t("landing.banners.reviewNow")}
              bannerTitle={t("landing.banners.failedPayment.title")}
              bannerActionOnClick={() => {
                if (auth?.access_token) {
                  TrackAnalyticsEvent(
                    AnalyticsEventNames.FailedPaymentBannerClicked,
                    auth.access_token,
                    auth?.user_id,
                    {
                      interaction_type: "button_click",
                      button_text: "REVIEW NOW",
                      element_name: "failed_payment_banner_clicked",
                      page_name: basePath,
                    },
                  );
                }
                history.push(`${BILLING_URL}/${failedPaymentBillPk}`);
              }}
            />
          )}
          {(() => {
            const bannerConfig = getBillingBannerConfig();
            return (
              bannerConfig && (
                <Banner
                  bannerType="warning"
                  bannerActionCTA={bannerConfig.actionText}
                  bannerTitle={bannerConfig.title}
                  bannerActionOnClick={() => {
                    if (auth?.access_token) {
                      TrackAnalyticsEvent(
                        AnalyticsEventNames.interaction,
                        auth?.access_token,
                        auth?.user_id,
                        {
                          interaction_type: "button_click",
                          button_text: bannerConfig.actionText,
                          element_name:
                            AnalyticsEventNames.MissingBillingInfoButtonClicked,
                          page_name: basePath,
                        },
                      );
                    }
                    setMissingBillingInfoModalOpen(true);
                  }}
                />
              )
            );
          })()}
          {ConsentForm}
          {showOutcomesBanner && Boolean(requisitions?.length) && (
            <Banner
              bannerType="information"
              bannerActionCTA={t("landing.banners.ok")}
              bannerText={t("landing.banners.outcomes.text")}
              bannerTitle={t("landing.banners.outcomes.title")}
              bannerActionOnClick={() => {
                if (auth?.access_token) {
                  TrackAnalyticsEvent(
                    AnalyticsEventNames.interaction,
                    auth?.access_token,
                    auth?.user_id,
                    {
                      interaction_type: "button_click",
                      button_text: "OK",
                      element_name:
                        AnalyticsEventNames.PatientOutcomesButtonClicked,
                      page_name: basePath,
                    },
                  );
                }
                dismissOutcomesBanner();
              }}
            />
          )}
          {Boolean(
            requisitions?.filter(
              (req) => req.portal_bill_status === "outstanding",
            ).length,
          ) && (
            <Banner
              bannerType="billing"
              bannerActionCTA={t("landing.banners.viewMyBill")}
              bannerText=""
              bannerTitle={t("landing.banners.billReady.title")}
              bannerActionOnClick={() => {
                if (auth?.access_token) {
                  TrackAnalyticsEvent(
                    AnalyticsEventNames.interaction,
                    auth?.access_token,
                    auth?.user_id,
                    {
                      interaction_type: "button_click",
                      button_text: "View My Bill",
                      element_name: AnalyticsEventNames.OpenBillButtonClicked,
                      page_name: basePath,
                    },
                  );
                }
                history.push(
                  multipleBills && multipleBills > 1
                    ? `${BILLING_MULTIPLE_URL}`
                    : `${BILLING_URL}/${
                        requisitions?.filter(
                          (req) => req.portal_bill_status === "outstanding",
                        )[selectedRequisition].bill_pk
                      }`,
                );
              }}
            />
          )}
          <LandingFeedbackBanner />
          <LandingPageTabletMobileRowWrapper
            $showStandaloneFetalSexCard={showStandaloneFetalSexCard}
            deviceType={deviceType}
            requisitionCount={requisitions?.length || 0}
            showBabyPeek={Boolean(
              requisitions &&
              requisitions[selectedRequisition]?.reveal_status === "pending",
            )}
            babyPeekAvailable={Boolean(
              requisitions &&
              (requisitions[selectedRequisition]?.reveal_status ===
                "available" ||
                requisitions[selectedRequisition]?.reveal_status === "removed"),
            )}
          >
            <LandingPageTitleWrapper>
              {GetTypographyContent({
                content: t("landing.sections.yourTests"),
                size: "h2",
                textColor: "greyMedium",
                isBold: true,
                padding: deviceType === "mobile" ? "0 0 0 15px" : "0",
              })}
            </LandingPageTitleWrapper>
            {requisitions && requisitions.length > 1 && RequisitionPicker}
            {specificRequisition && (
              <LandingGeneralContainer
                padding="0px"
                key={specificRequisition.req_identifier}
              >
                <TestResults
                  requisitionID={specificRequisition.req_identifier}
                  standaloneFetalSexExperience={standaloneFetalSexExperience}
                />
              </LandingGeneralContainer>
            )}
            {specificRequisition && showStandaloneFetalSexCard && (
              <StandaloneFetalSexCard
                key={`standalone-fetal-sex-${specificRequisition.req_identifier}`}
                requisitionID={specificRequisition.req_identifier}
              />
            )}

            {Boolean(
              requisitions &&
              requisitions[selectedRequisition]?.reveal_status === "available",
            ) && (
              <LandingGeneralContainer padding="0px">
                {requisitions &&
                requisitions[selectedRequisition]?.reveal_status ===
                  "removed" ? (
                  <BabyPeekToolTip
                    onClick={() => setTooltip(!tooltip)}
                    PopperProps={
                      {
                        sx: {
                          "& .MuiTooltip-tooltip": {
                            backgroundColor: "white",
                            boxShadow: "0px 10px 20px rgb(0 0 0 / 10%)",
                            color: "black",
                            fontSize: "16px",
                            fontWeight: 400,
                            lineHeight: "150%",
                            textAlign: "center",
                            border: "1px solid #cbd5e0",
                          },
                        },
                        open: tooltip,
                      } as Partial<PopperProps>
                    }
                    title={babyPeekUnavailableText}
                  >
                    <div>
                      {t("landing.babyPeek.unavailableShort")} <InfoOutlined />
                    </div>
                  </BabyPeekToolTip>
                ) : (
                  <BabyPeekAvailable>
                    <div>
                      <img src={BabyPeekHeader} alt="BabyPeekHeader" />
                      <div>BabyPeek</div>
                    </div>
                    <div>
                      <BabyPeekButton
                        variant="filled"
                        content={<>{t("landing.babyPeek.viewResults")}</>}
                        onClick={() =>
                          history.push(
                            `${BABYPEEK_URL}/${
                              requisitions &&
                              requisitions[selectedRequisition]?.baby_peek_pk
                            }/${
                              babyPeekStatusFlag === "available"
                                ? "summary"
                                : "intro"
                            }?loc=dashboard`,
                          )
                        }
                      />
                    </div>
                  </BabyPeekAvailable>
                )}
              </LandingGeneralContainer>
            )}
            {requisitions &&
              requisitions[selectedRequisition]?.reveal_status ===
                "pending" && (
                <DetailedResultsShareBabyPeek
                  babyPeekPK={requisitions[selectedRequisition]?.baby_peek_pk}
                  page="Dashboard"
                />
              )}
            <LandingGeneralContainer
              padding={deviceType === "mobile" ? "16px" : "20px"}
            >
              <VideoEmbed
                overlayText={
                  <>
                    {t("landing.videoOverlay.prefix")}Unity Complete
                    <sup>&reg;</sup>
                    {t("landing.videoOverlay.suffix")}
                  </>
                }
                requisitionID={
                  (requisitions &&
                    requisitions[selectedRequisition]?.req_identifier) ||
                  ""
                }
              />
            </LandingGeneralContainer>

            <LandingFeedbackCard
              buttonSize={deviceType === "mobile" ? "small" : "medium"}
            />

            <LandingGeneralContainer
              padding={deviceType === "mobile" ? "16px" : "0px"}
            >
              {shouldBlockBooking ? (
                <Card
                  {...landingPageGCLearnMoreCard(deviceType, t)}
                  buttons={[
                    {
                      buttonAction: () => {
                        TrackAnalyticsEvent(
                          AnalyticsEventNames.GCLearnMoreButtonClicked,
                          auth?.access_token,
                        );
                        history.push(GC_LEARN_MORE_URL);
                      },
                      buttonText: t("landing.gcCard.learnMore"),
                    },
                  ]}
                />
              ) : (
                <Card
                  {...landingPageCard(deviceType, t)}
                  buttons={[
                    {
                      buttonAction: () => history.push("scheduling"),
                      buttonText: t("landing.gcCard.bookConsultation"),
                    },
                  ]}
                />
              )}
            </LandingGeneralContainer>

            <LandingPageDesktopRightWrapper>
              <LandingPageTitleWrapper>
                {GetTypographyContent({
                  content: t("landing.sections.haveAQuestion"),
                  size: "xlarge",
                  textColor: "greyMedium",
                  isBold: true,
                  padding: deviceType === "mobile" ? "0 0 0 15px" : "0",
                })}
              </LandingPageTitleWrapper>
              <LandingGeneralContainer padding="0px">
                <LandingPageAccordionWrapper>
                  <Accordions
                    data={landingPageAccordions(
                      (requisitions &&
                        requisitions[selectedRequisition]?.req_identifier) ??
                        "",
                      t,
                    )}
                    isLinked={deviceType !== "mobile"}
                    size="small"
                  />
                  <LinkedAccordian
                    id="help"
                    key="help"
                    panelKey="help"
                    title={t("landing.faq.visitFaqs")}
                    onClick={() => {
                      history.push(HELP_PAGE_URL);
                    }}
                    size="large"
                  />
                </LandingPageAccordionWrapper>
              </LandingGeneralContainer>
            </LandingPageDesktopRightWrapper>
          </LandingPageTabletMobileRowWrapper>
        </LandingPageTabletMobileWrapper>
      </LandingPageWrapper>
    );
  };

  return (
    <>
      {showBabyPeekPopUp && <BabyPeekOptIn />}
      {requisitions?.[selectedRequisition] && (
        <ProfileAlabamaWarning
          alabamaModalOpen={showABNFormPatient.show}
          setAlabamaModalOpen={(show: boolean) =>
            setShowABNFormPatient({ show, loaded: true })
          }
          requisition={requisitions?.[selectedRequisition]}
        />
      )}

      <MissingBillingInformationModal
        isOpen={missingBillingInfoModalOpen}
        onClose={() => setMissingBillingInfoModalOpen(false)}
        missingInsurance={isMissingBillingInformation(patientInfo)}
        missingAddress={isMissingBillingAddress(patientInfo)}
      />

      {getLandingContent()}
    </>
  );
};

export default Landing;
